"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  IconButton,
  Collapse,
  Box,
  Typography,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Chip,
  Tooltip,
  Button,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material"
import { useTranslation } from "../common/components/LocalizationProvider"
import { formatDate } from "../common/util/formatter"
import { errorsActions, sessionActions } from "../store"
import PageLayout from "../common/components/PageLayout"
import ReportFilter from "../reports/components/ReportFilter"
import ColumnSelect from "../reports/components/ColumnSelect"
import TableShimmer from "../common/components/TableShimmer"
import useReportStyles from "../reports/common/useReportStyles"
import { useCatch } from "../reactHelper"
import { utils, writeFileXLSX } from "xlsx"
import { nativePostMessage } from "../common/components/NativeInterface"
import ReportsMenu from "./components/ReportsMenu"
import UserNameFilter from "./components/UserNameFilter"
import fetchOrThrow from "../common/util/fetchOrThrow"
import PrintHeader from "./common/PrintHeader"
import type { SelectChangeEvent } from "@mui/material/Select"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import RestoreIcon from "@mui/icons-material/Restore"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DownloadIcon from "@mui/icons-material/Download"
import RecoveryDialog from "./components/RecoveryDialog"
import { AuditLogItem, AuditLogDiff, AuditLogAttributes } from "./types"

interface User {
  id: number
  name?: string
  email?: string
}

// A stable empty object used as a fallback for usersMap selectors.
// Returning the same reference avoids creating a new object on each render,
// preventing unnecessary re-renders when the state.usersMap is undefined.
const EMPTY_USERS_MAP = Object.freeze<Record<number, string | undefined>>({})
const EMPTY_DEVICE_MAP = Object.freeze<Record<number, any>>({})
const FILTER_FORM_CONTROL_SX = {
  minWidth: 240,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "primary.main" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
}
const FILTER_SELECT_SX = { backgroundColor: "background.paper" }

const AttributesRenderer = ({ attributes }: { attributes: AuditLogAttributes }) => {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const diff = attributes?.diff as AuditLogDiff | undefined

  const baseAttributes: Record<string, any> = { ...attributes }
  delete baseAttributes.diff

  const hasDiff =
    diff !== undefined &&
    ((diff.attributes && Object.keys(diff.attributes).length > 0) ||
      (diff.old && Object.keys(diff.old).length > 0) ||
      (diff.permissions && Object.keys(diff.permissions).length > 0))

  if (!hasDiff && Object.keys(baseAttributes).length === 0) {
    return <span>-</span>
  }

  const normalizedAttributes: Record<string, any> = {}
  Object.entries(baseAttributes).forEach(([key, value]) => {
    if (typeof value === "string" && (value.startsWith("[") || value.startsWith("{"))) {
      try {
        normalizedAttributes[key] = JSON.parse(value)
      } catch (error) {
        normalizedAttributes[key] = value
      }
    } else {
      normalizedAttributes[key] = value
    }
  })

  const preStyle = {
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.75rem",
    fontFamily: "monospace",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    padding: "8px",
    borderRadius: "4px",
    maxHeight: "220px",
    overflow: "auto",
  }

  const renderPre = (payload: Record<string, any>) => <pre style={preStyle}>{JSON.stringify(payload, null, 2)}</pre>

  const payloadForCopy = hasDiff ? diff : normalizedAttributes

  const handleCopy = async () => {
    if (!navigator.clipboard) {
      return
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(payloadForCopy, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      setCopied(false)
    }
  }

  const summaryLabel = hasDiff
    ? "Visualizar diff"
    : `Visualizar ${Object.keys(normalizedAttributes).length} atributo${
        Object.keys(normalizedAttributes).length !== 1 ? "s" : ""
      }`

  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          aria-label={open ? "Minimizar detalhes" : "Expandir detalhes"}
          size="small"
          onClick={() => setOpen(!open)}
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Typography variant="body2">{open ? "Minimizar" : summaryLabel}</Typography>
        {copied && <Chip label="Copiado" size="small" color="success" />}
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {hasDiff ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Valores atuais</Typography>
                {renderPre(diff?.attributes ?? {})}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Valores anteriores</Typography>
                {renderPre(diff?.old ?? {})}
              </Box>
            </Stack>
            {diff?.permissions && Object.keys(diff.permissions).length > 0 && (
              <Box>
                <Typography variant="subtitle2">Permissões</Typography>
                {renderPre(diff.permissions)}
              </Box>
            )}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={handleCopy}
              >
                {copied ? "Copiado" : "Copiar JSON"}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <pre style={preStyle}>{JSON.stringify(normalizedAttributes, null, 2)}</pre>
        )}
      </Collapse>
    </div>
  )
}

const ActionsReportPage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const classes = useReportStyles()
  const t = useTranslation()
  const [logs, setLogs] = useState<AuditLogItem[]>([])
  const [todosLogs, setTodosLogs] = useState<AuditLogItem[]>([])
  const [loading, setLoading] = useState(false)
  const [usersLoading, setUsersLoading] = useState(false)
  const [hasResults, setHasResults] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [userIdFilter, setUserIdFilter] = useState("")
  const [deviceIdFilter, setDeviceIdFilter] = useState("")
  const [periodoFilter, setPeriodoFilter] = useState<{ from: string; to: string }>({ from: "", to: "" })
  const [usuarios, setUsuarios] = useState<User[]>([])
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const printing = useSelector((state) => state.session.printing)
  const usersMap = useSelector((state) => state.usersMap ?? EMPTY_USERS_MAP, shallowEqual)
  const devices = useSelector((state) => state.devices?.items ?? EMPTY_DEVICE_MAP, shallowEqual)

  const [available, setAvailable] = useState([])
  const [columns, setColumns] = useState([
    "id",
    "userId",
    "actionType",
    "objectType",
    "address",
    "actionTime",
    "attributes",
    "actions",
  ])
  const [showRemovalsOnly, setShowRemovalsOnly] = useState(false)
  const [selectedAction, setSelectedAction] = useState<AuditLogItem | null>(null)
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [feedback, setFeedback] =
    useState<{ open: boolean; message: string; severity: "success" | "error" }>({
      open: false,
      message: "",
      severity: "success",
    })

  const selectedUserName = userIdFilter ? usersMap[userIdFilter] ?? userIdFilter : undefined
  const selectedDeviceName = deviceIdFilter
    ? devices[Number(deviceIdFilter)]?.name || devices[Number(deviceIdFilter)]?.uniqueId || deviceIdFilter
    : undefined

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t("systemLogReport")}.pdf`)),
      )
    }
  }, [printing, t])

  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false))
    }
    window.addEventListener("afterprint", handleAfterPrint)
    return () => {
      window.removeEventListener("afterprint", handleAfterPrint)
    }
  }, [])

  useEffect(() => {
    const fetchUsers = async () => {
      const limit = 500
      try {
        setUsersLoading(true)
        // standardized filter section: carrega usuários acessíveis para dropdown otimizado.
        const response = await fetchOrThrow(`/api/users?all=false&limit=${limit}`) // switched to fetchOrThrow
        const data: User[] = await response.json()

        const userNamesMap = {}
        const sanitizedUsers: User[] = []
        data.forEach((user) => {
          if (typeof user.id !== "number") {
            return
          }
          const displayName = user.name || user.email || `${user.id}`
          userNamesMap[user.id] = displayName
          sanitizedUsers.push({ id: user.id, name: displayName, email: user.email })
        })

        dispatch({
          type: "usersMap/update",
          payload: userNamesMap,
        })

        setUsuarios(sanitizedUsers)

        if (sanitizedUsers.length >= limit) {
          console.warn(
            `Limite de ${limit} usuários atingido ao carregar opções de filtro. Considere refinar os critérios.`,
          )
        }
      } catch (error) {
        console.error("Erro ao buscar usuários:", error)
      } finally {
        setUsersLoading(false)
      }
    }

    fetchUsers()
  }, [dispatch])

  // preserved custom logic: mantém agregações locais e exportações específicas enquanto padroniza filtros.
  const buscarLogs = async (filtros: Record<string, any> = {}) => {
    setHasResults(false)
    setLoading(true)
    try {
      const filtrosComUsuario = { ...filtros }
      if (!filtrosComUsuario.userId && userIdFilter) {
        filtrosComUsuario.userId = userIdFilter
      }
      if (!filtrosComUsuario.deviceId && deviceIdFilter) {
        filtrosComUsuario.deviceId = deviceIdFilter
      }

      const query = new URLSearchParams()
      Object.entries(filtrosComUsuario).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "" || value === false) {
          return
        }

        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null && item !== "") {
              query.append(key, item.toString())
            }
          })
        } else {
          query.append(key, value.toString())
        }
      })

      const periodoAplicado = {
        from: filtrosComUsuario.from ?? periodoFilter.from,
        to: filtrosComUsuario.to ?? periodoFilter.to,
      }

      const endpoint = query.toString() ? `/api/actions?${query.toString()}` : "/api/actions"
      const resposta = await fetchOrThrow(endpoint) // switched to fetchOrThrow
      const dados = await resposta.json()
      setTodosLogs(dados)

      const usuarioAplicado = Array.isArray(filtrosComUsuario.userId)
        ? filtrosComUsuario.userId[0]?.toString() ?? ""
        : filtrosComUsuario.userId?.toString() ?? userIdFilter
      const dispositivoAplicado = Array.isArray(filtrosComUsuario.deviceId)
        ? filtrosComUsuario.deviceId[0]?.toString() ?? ""
        : filtrosComUsuario.deviceId?.toString() ?? deviceIdFilter

      const dadosFiltrados = aplicarFiltrosLocais(
        dados,
        usuarioAplicado,
        periodoAplicado,
        showRemovalsOnly,
        dispositivoAplicado,
      )
      setLogs(dadosFiltrados)
      setHasResults(dadosFiltrados.length > 0)

      setPage(0)

      if (dados.length > 0) {
        const keySet = new Set()
        dados.forEach((log: AuditLogItem) => {
          Object.keys(log).forEach((key) => keySet.add(key))
        })
        keySet.add("actions")

        setAvailable(
          [...keySet]
            .map((key) => [key, getColumnDisplayName(key as string)])
            .sort((a, b) => a[1].localeCompare(b[1])),
        )
      }
    } catch (erro) {
      if (erro instanceof Error) {
        dispatch(errorsActions.push(erro.message))
      }
      setHasResults(false)
    } finally {
      setLoading(false)
    }
  }

  const aplicarFiltrosLocais = (
    logs: AuditLogItem[],
    userId: string,
    periodo: { from: string; to: string },
    removalsOnly = showRemovalsOnly,
    deviceId: string = deviceIdFilter,
  ) => {
    // preserved custom logic: filtragem adicional em memória para recursos existentes (ex.: impressões, destaque de remoções).
    let resultado = [...logs]

    if (userId) {
      resultado = resultado.filter((log) => log.userId.toString() === userId)
    }

    if (deviceId) {
      resultado = resultado.filter(
        (log) =>
          typeof log.objectType === "string" &&
          log.objectType.toLowerCase() === "device" &&
          log.objectId?.toString() === deviceId,
      )
    }

    if (periodo.from || periodo.to) {
      resultado = resultado.filter((log) => {
        const dataLog = new Date(log.actionTime)

        if (periodo.from && periodo.to) {
          const dataInicio = new Date(periodo.from)
          const dataFim = new Date(periodo.to)
          dataFim.setHours(23, 59, 59, 999)

          return dataLog >= dataInicio && dataLog <= dataFim
        } else if (periodo.from) {
          const dataInicio = new Date(periodo.from)
          return dataLog >= dataInicio
        } else if (periodo.to) {
          const dataFim = new Date(periodo.to)
          dataFim.setHours(23, 59, 59, 999)
          return dataLog <= dataFim
        }

        return true
      })
    }

    if (removalsOnly) {
      resultado = resultado.filter((log) => log.actionType === "remove")
    }

    return resultado
  }

  const filtrarPorUserId = (userId) => {
    setUserIdFilter(userId)
    const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userId, periodoFilter, showRemovalsOnly, deviceIdFilter)
    setLogs(dadosFiltrados)
    setHasResults(dadosFiltrados.length > 0)
    setPage(0)
  }

  const handleToggleRemovals = (checked: boolean) => {
    setShowRemovalsOnly(checked)
    const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userIdFilter, periodoFilter, checked, deviceIdFilter)
    setLogs(dadosFiltrados)
    setHasResults(dadosFiltrados.length > 0)
    setPage(0)
  }

  const handleDeviceFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value ?? ""
    setDeviceIdFilter(value)
    const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userIdFilter, periodoFilter, showRemovalsOnly, value)
    setLogs(dadosFiltrados)
    setHasResults(dadosFiltrados.length > 0)
    setPage(0)
  }

  const openRestoreDialog = (log: AuditLogItem) => {
    setSelectedAction(log)
    setRestoreDialogOpen(true)
  }

  const handleDialogClose = () => {
    setRestoreDialogOpen(false)
    setSelectedAction(null)
  }

  const handleRestored = (message?: string) => {
    setFeedback({ open: true, message: message || "Registro restaurado com sucesso.", severity: "success" })
    buscarLogs()
    handleDialogClose()
  }

  const handleRestoreError = (message: string) => {
    setFeedback({ open: true, message, severity: "error" })
  }

  const handleFeedbackClose = () => {
    setFeedback((prev) => ({ ...prev, open: false }))
  }

  const exportRemovals = () => {
    const removals = logs.filter((log) => log.actionType === "remove")
    if (!removals.length) {
      setFeedback({ open: true, message: "Nenhuma exclusão disponível para exportar.", severity: "error" })
      return
    }

    const dataset = removals.map((log) => ({
      ID: log.id,
      Usuário: usersMap[log.userId] || log.userId,
      Objeto: `${log.objectType ?? "-"}#${log.objectId ?? "-"}`,
      "Data e Hora": formatarDataHora(log.actionTime),
      Detalhes: formatAttributesForPrinting(log.attributes),
    }))

    const worksheet = utils.json_to_sheet(dataset)
    const workbook = utils.book_new()
    utils.book_append_sheet(workbook, worksheet, "Exclusoes")
    writeFileXLSX(workbook, "exclusoes_auditoria.xlsx")
  }

  const handleExportReport = (_params: { from?: string; to?: string } = {}) => {
    // preserved custom logic – existing export logic baseado na tabela renderizada.
    if (!logs.length) {
      setFeedback({ open: true, message: "Nenhum registro disponível para exportação.", severity: "error" })
      return
    }
    const table = document.querySelector("table")
    if (!table) {
      setFeedback({ open: true, message: "Tabela não encontrada para exportação.", severity: "error" })
      return
    }
    requestAnimationFrame(() => writeFileXLSX(utils.table_to_book(table), "logs_auditoria.xlsx"))
  }

  const handlePrintReport = (params: { from?: string; to?: string; [key: string]: unknown } = {}) => {
    // standardized export/print/agenda menu – integra fluxo de impressão compartilhado.
    const { from: fromParam, to: toParam } = params
    if (!logs.length) {
      setFeedback({ open: true, message: "Nenhum registro disponível para impressão.", severity: "error" })
      return
    }
    const periodoInicial = fromParam || periodoFilter.from || logs[0]?.actionTime || ""
    const periodoFinal = toParam || periodoFilter.to || logs[logs.length - 1]?.actionTime || ""
    if (periodoInicial) {
      setFrom(periodoInicial)
    }
    if (periodoFinal) {
      setTo(periodoFinal)
    }
    dispatch(sessionActions.setPrinting(true))
  }

  const getColumnDisplayName = (column: string): string => {
    switch (column) {
      case "id":
        return "ID"
      case "userId":
        return "Usuário"
      case "actionType":
        return "Tipo de Ação"
      case "objectType":
        return "Tipo de Objeto"
      case "objectId":
        return "ID do Objeto"
      case "address":
        return "Endereço IP"
      case "actionTime":
        return "Data e Hora"
      case "attributes":
        return "Detalhes"
      case "actions":
        return "Ações"
      default:
        return column
    }
  }

  useEffect(() => {
    buscarLogs()
  }, [])

  const handleChangePage = (event: unknown, novaPagina: number) => {
    setPage(novaPagina)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const formatarDataHora = (stringDataHora: string): string => {
    // Convert to Brasilia time then format via centralized formatDate
    const dateInBrasilia = new Date(new Date(stringDataHora).toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }))
    return formatDate(dateInBrasilia.toISOString())
  }

  const formatAttributesForPrinting = (attributes: AuditLogAttributes) => {
    if (!attributes) {
      return "-"
    }
    const diff = attributes.diff
    if (diff) {
      const sections: string[] = []
      if (diff.old && Object.keys(diff.old).length > 0) {
        sections.push(`Antes:\n${JSON.stringify(diff.old, null, 2)}`)
      }
      if (diff.attributes && Object.keys(diff.attributes).length > 0) {
        sections.push(`Depois:\n${JSON.stringify(diff.attributes, null, 2)}`)
      }
      if (diff.permissions && Object.keys(diff.permissions).length > 0) {
        sections.push(`Permissões:\n${JSON.stringify(diff.permissions, null, 2)}`)
      }
      return sections.length ? sections.join("\n\n") : "-"
    }
    const baseAttributes = { ...attributes }
    delete baseAttributes.diff
    return Object.keys(baseAttributes).length ? JSON.stringify(baseAttributes, null, 2) : "-"
  }

  const renderCellValue = (log: AuditLogItem, column: string) => {
    switch (column) {
      case "id":
        return log.id
      case "userId":
        return usersMap[log.userId] || `${log.userId}`
      case "actionType":
        if (log.actionType === "remove") {
          return <Chip label="Remoção" size="small" color="error" />
        }
        return log.actionType
      case "objectType":
        return log.objectType || "-"
      case "objectId":
        return log.objectId || "-"
      case "address":
        return log.address
      case "actionTime":
        return formatarDataHora(log.actionTime)
      case "attributes":
        if (printing) {
          return formatAttributesForPrinting(log.attributes)
        }

        return <AttributesRenderer attributes={log.attributes} />
      case "actions":
        if (log.actionType === "remove" && log.attributes?.diff) {
          return (
            <Tooltip title="Restaurar registro">
              <span>
                <IconButton size="small" color="primary" onClick={() => openRestoreDialog(log)}>
                  <RestoreIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
          )
        }
        return "-"
      default:
        return log[column] !== undefined ? log[column] : "-"
    }
  }

  const handleSubmit = useCatch(async ({ from, to }) => {
    const usuarioSelecionado = userIdFilter
    const dispositivoSelecionado = deviceIdFilter

    if (!usuarioSelecionado && !dispositivoSelecionado) {
      setFeedback({
        open: true,
        message: "Selecione ao menos um usuário ou dispositivo para gerar o relatório.",
        severity: "error",
      })
      setLogs([])
      setTodosLogs([])
      setAvailable([])
      setHasResults(false)
      setPage(0)
      return
    }

    const periodoAtualizado = { from: from ?? "", to: to ?? "" }
    setPeriodoFilter(periodoAtualizado)
    setLogs([])
  setTodosLogs([])
  setAvailable([])
  setHasResults(false)
  setPage(0)

  const filtros: Record<string, any> = {}
  if (periodoAtualizado.from) filtros.from = periodoAtualizado.from
  if (periodoAtualizado.to) filtros.to = periodoAtualizado.to
  if (usuarioSelecionado) filtros.userId = usuarioSelecionado
  if (dispositivoSelecionado) filtros.deviceId = dispositivoSelecionado

  await buscarLogs(filtros)
})

  const handleSchedule = useCatch(async (userIds, groupIds, report) => {
    report.type = "logs"
    navigate("/reports/scheduled")
  })


  if (printing) {
    return (
      <div>
        <PrintHeader
          title={t("systemLogReport")}
          from={from}
          to={to}
          user={selectedUserName}
          device={selectedDeviceName}
        />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{getColumnDisplayName(key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  {columns.map((column) => (
                    <TableCell key={`${log.id}-${column}`}>{renderCellValue(log, column)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={["Sistema", "Logs de Ações"]}>
      <div className={classes.container}>
        <div className={classes.containerMain}>
          <div className={classes.header}>
            {/* standardized export/print/agenda menu */}
            <ReportFilter
              onShow={handleSubmit}
              onExport={handleExportReport}
              onPrint={handlePrintReport}
              onSchedule={handleSchedule}
              loading={loading}
              canRun={Boolean(userIdFilter || deviceIdFilter)}
              hasResults={hasResults}
              ignoreDevice
              showOnly={null}
              includeExportOptions
            >
              <>
                <Box className={classes.filterItem} sx={{ order: -2, minWidth: 200 }}>
                  <FormControl fullWidth size="small" variant="outlined" sx={FILTER_FORM_CONTROL_SX}>
                    <InputLabel id="device-select-label">{t("sharedDevice")}</InputLabel>
                    <Select
                      labelId="device-select-label"
                      value={deviceIdFilter}
                      label={t("sharedDevice")}
                      onChange={handleDeviceFilterChange}
                      displayEmpty
                      size="small"
                      sx={FILTER_SELECT_SX}
                    >
                      <MenuItem value="">
                        <em>{t("sharedAllDevices") || "Todos os dispositivos"}</em>
                      </MenuItem>
                      {Object.values(devices).map((device: any) => (
                        <MenuItem key={device.id} value={device.id?.toString() ?? ""}>
                          {device.name || device.uniqueId || device.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box className={classes.filterItem} sx={{ order: -1, minWidth: 200 }}>
                  {usersLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <UserNameFilter onChange={filtrarPorUserId} users={usuarios} value={userIdFilter} />
                  )}
                </Box>

                <Box className={classes.filterItem} sx={{ order: 1, minWidth: 200 }}>
                  <ColumnSelect
                    columns={columns}
                    setColumns={setColumns}
                    columnsArray={available}
                    rawValues
                    disabled={!logs.length}
                  />
                </Box>

                <Box className={classes.filterItem} sx={{ order: 2, minWidth: 200 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showRemovalsOnly}
                        onChange={(event) => handleToggleRemovals(event.target.checked)}
                      />
                    }
                    label="Mostrar apenas exclusões"
                    sx={{ whiteSpace: "nowrap" }}
                  />
                </Box>

                <Box className={classes.filterItem} sx={{ order: 3, display: "flex", justifyContent: "flex-end", minWidth: 200 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<DownloadIcon />}
                    onClick={exportRemovals}
                    disabled={!logs.some((log) => log.actionType === "remove")}
                    sx={{ minWidth: 160 }}
                  >
                    Exportar exclusões
                  </Button>
                </Box>
              </>
            </ReportFilter>
          </div>

          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{getColumnDisplayName(key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? (
                logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log) => (
                  <TableRow
                    key={log.id}
                    hover
                    sx={log.actionType === "remove" ? { backgroundColor: "rgba(244, 67, 54, 0.08)" } : undefined}
                  >
                    {columns.map((column) => (
                      <TableCell key={`${log.id}-${column}`}>{renderCellValue(log, column)}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableShimmer columns={columns.length} />
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={logs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
          <RecoveryDialog
            open={restoreDialogOpen}
            action={selectedAction}
            onClose={handleDialogClose}
            onRestored={handleRestored}
            onError={handleRestoreError}
          />
          <Snackbar
            open={feedback.open}
            autoHideDuration={4000}
            onClose={handleFeedbackClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert onClose={handleFeedbackClose} severity={feedback.severity} sx={{ width: "100%" }}>
              {feedback.message}
            </Alert>
          </Snackbar>
        </div>
      </div>
    </PageLayout>
  )
}

export default ActionsReportPage
